import type { ComponentProps, FC } from 'react';
import React, { useState, useMemo, useCallback } from 'react';
import cn from 'classnames';

import { AnalyticsSpy } from './components/analytics-spy';
import type { TX5BankFormShort } from './models';
import { convertCardsConfig, getQueriesPassedToForm } from './utils/helpers';
import type { TResidentValue } from './models/analytics';
import { analyticsX5Form } from './config';

export const X5BankForm: FC<TX5BankFormShort> = ({
    Agreement,
    Birthday,
    Button,
    Heading,
    PromoCode,
    DcDraft,
    heading,
    Email,
    FioField,
    Form,
    Grid,
    Layout,
    Link,
    Phone,
    Progress,
    RadioTags,
    SecurityWarrantyBlock,
    Text,
    StickyMobile,
    analytics = analyticsX5Form,
    birthday,
    buttonText,
    cards,
    draft = {},
    email,
    formHash,
    fio,
    isPhoneFirst,
    formAction = {},
    useVisibilityChecker,
    isMobile,
    notResident,
    className,
    oneFieldFio = true,
    phone,
    product = 'dc-x5',
    progress,
    resident,
    securityText = 'Ваши данные в безопасности',
    styles,
    sopdType = 'general',
    promoCode,
    refId = '',
    useNamedContainers,
    sendEvent,
    children,
    stickyMobile,
    queryNames = [
        'platformId',
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_content',
        'utm_term',
    ],
    ...rest
}) => {
    const [isResident, setIsResident] = useState<TResidentValue>('resident');
    const { sopdContent } = useNamedContainers(children);

    // TODO Вынести логику в функцию
    // Определяем значения cardId, packetId, prefillContractId для передачи в квери параметрах
    const [paySystem] = Object.keys(cards);
    const { card, cardId, packetId, prefillContractId } = cards[paySystem][0].cardParams;
    const cardParams = { cardId, packetId, prefillContractId, card };

    const onVisible = useCallback(() => {
        if (analytics?.scrollToButton) {
            sendEvent(analytics?.scrollToButton);
        }
    }, [analytics, sendEvent]);

    const securityRef = useVisibilityChecker(onVisible);

    const queriesPassedToForm = useMemo(
        () => getQueriesPassedToForm({ ...rest }, queryNames),
        [rest, queryNames]
    );

    const handleResident = () => {
        sendEvent(analytics.resident?.buttonResidentClickToMain);
    };

    const handleSetResident = (value: TResidentValue) => {
        setIsResident(value as TResidentValue);
        sendEvent(
            value === 'resident'
                ? analytics?.resident?.buttonResidentClickYes
                : analytics?.resident?.buttonResidentClickNo
        );
    };

    const oneFieldFioProps: ComponentProps<typeof FioField> = {
        ...fio,
        oneFieldFio,
        fioCaption: fio.caption,
        fioPlaceholder: fio.placeholder,
        genderTagsSize: 'xs',
        checkboxColor: 'green',
        name: {
            lastName: 'lastName',
            firstName: 'firstName',
            middleName: 'middleName',
            sex: 'sex',
        },
    };

    const phoneProps: ComponentProps<typeof Phone> = {
        name: 'phone',
        settings: phone,
        styler: { marginBottom: 24 },
    };

    const birthdayProps: ComponentProps<typeof Birthday> = {
        ageLimit: birthday?.ageLimit,
        name: 'passportBirthDate',
        settings: birthday,
        styler: { marginBottom: 24 },
    };

    return (
        <DcDraft {...draft} cards={convertCardsConfig(cards)}>
            <Form
                analytics={{
                    submit: analytics?.submit?.success,
                    submitOnFail: analytics?.submit?.fail,
                }}
                businessLoggingSource="FormSenderDc"
                className={cn(styles.x5BankForm, className)}
                formAction={{ ...formAction, ...cardParams, refId, ...queriesPassedToForm }}
                formHash={formHash}
                product={product}
            >
                {analytics && <AnalyticsSpy analytics={analytics} />}

                <Heading heading={heading} />

                {progress && <Progress {...progress} />}

                {resident && (
                    <RadioTags
                        caption={resident.caption}
                        name="resident"
                        onSelect={({ value }) => handleSetResident(value as TResidentValue)}
                        selected={isResident}
                        size="xs"
                        styler={{ marginBottom: 24 }}
                        tags={resident.tags}
                        titleClassName={cn(styles.external, styles.X5residentCaption)}
                    />
                )}

                <div
                    style={{
                        display: isResident === 'resident' ? 'block' : 'none',
                    }}
                >
                    {isPhoneFirst ? <Phone {...phoneProps} /> : <FioField {...oneFieldFioProps} />}

                    {isPhoneFirst ? (
                        <FioField {...oneFieldFioProps} />
                    ) : (
                        birthday && <Birthday {...birthdayProps} />
                    )}

                    <Grid vertical={isMobile}>
                        {isPhoneFirst ? (
                            birthday && <Birthday {...birthdayProps} />
                        ) : (
                            <Phone {...phoneProps} />
                        )}

                        <Email
                            name="email"
                            optional={email.optional}
                            settings={email}
                            styler={{ marginBottom: 24 }}
                        />
                    </Grid>

                    {promoCode && <PromoCode name="promocode" settings={promoCode} />}

                    {sopdType === 'general' && (
                        <Agreement name="sopdConfirmed" styler={{ marginBottom: 24 }} />
                    )}

                    <div ref={securityRef} />
                    <SecurityWarrantyBlock
                        buttonDataTestId={'X5SecurityButton'}
                        buttonInnerClassName={cn(styles.external, styles.X5SecurityButton)}
                        buttonText={buttonText}
                        className={cn(styles.external, styles.X5SecurityWarrantyBlock)}
                        content={securityText}
                    />
                    {sopdType === 'custom' && (
                        <Text
                            styler={{ marginBottom: 16, marginTop: 24 }}
                            view={isMobile ? 'primary-small' : 'primary-medium'}
                        >
                            {sopdContent}
                        </Text>
                    )}
                </div>
                {isResident !== 'resident' && notResident && (
                    <div
                        className={cn({
                            [styles.notResidentBlock]: !isMobile,
                        })}
                    >
                        <Text styler={{ marginBottom: 24, marginTop: 0 }} view="primary-small">
                            {notResident.text}
                        </Text>
                        <Link
                            dataTestId="not-resident-link"
                            href={notResident.buttonLink}
                            noUnderline
                            onClick={handleResident}
                        >
                            <Button block hasAnimation size="s" view="primary">
                                {notResident.buttonText}
                            </Button>
                        </Link>
                    </div>
                )}
                {stickyMobile && <StickyMobile {...stickyMobile} />}
            </Form>
        </DcDraft>
    );
};
